import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Send, Eye, Users } from "lucide-react";

export default function MassBlastPage() {
  const { user } = useAuth();
  const [form, setForm] = useState({ subject: "", body: "", inquiry_type: "all", brand_filter: "all" });
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handlePreview = async () => {
    if (!form.subject || !form.body) { toast.error("Subject and body required"); return; }
    try {
      const { data } = await api.post("/blast/preview", { inquiry_type: form.inquiry_type, brand_filter: form.brand_filter });
      setPreview(data);
    } catch { toast.error("Preview failed"); }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/blast/send", form);
      toast.success(`Blast sent to ${data.sent} recipients!`);
      setShowConfirm(false);
      setPreview(null);
    } catch (err) { toast.error(err.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-4 max-w-3xl" data-testid="mass-blast-page">
      <h1 className="font-outfit text-2xl font-bold tracking-tight">Mass Email Blast</h1>

      <Card className="border-border">
        <CardContent className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Filter by Type</label>
              <Select value={form.inquiry_type} onValueChange={(v) => setForm({ ...form, inquiry_type: v })}>
                <SelectTrigger data-testid="blast-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="rental">Rental</SelectItem>
                  <SelectItem value="birthday_party">Birthday Party</SelectItem>
                  <SelectItem value="picnic">Picnic</SelectItem>
                  <SelectItem value="event">Event</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Filter by Brand</label>
              <Select value={form.brand_filter} onValueChange={(v) => setForm({ ...form, brand_filter: v })}>
                <SelectTrigger data-testid="blast-brand"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Both</SelectItem>
                  <SelectItem value="rrdc">Rolling River</SelectItem>
                  <SelectItem value="riverview">Riverview</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">Subject</label>
            <Input data-testid="blast-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Email subject line" />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">Body</label>
            <textarea
              data-testid="blast-body"
              className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring min-h-[160px]"
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="Write your email content here..."
            />
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={handlePreview} data-testid="blast-preview-btn">
              <Eye size={14} /> Preview Recipients
            </Button>
          </div>

          {preview && (
            <div className="p-4 rounded-lg border border-border bg-muted/30 space-y-2">
              <div className="flex items-center gap-2">
                <Users size={16} className="text-primary" />
                <span className="font-medium text-sm">{preview.count} recipients</span>
              </div>
              <div className="text-xs text-muted-foreground max-h-32 overflow-y-auto space-y-0.5">
                {preview.recipients.map((r, i) => (
                  <div key={i}>{r.first_name} {r.last_name} — {r.email}</div>
                ))}
                {preview.count > 50 && <div className="font-medium">...and {preview.count - 50} more</div>}
              </div>
              <Button className="w-full mt-2" onClick={() => setShowConfirm(true)} data-testid="blast-send-btn">
                <Send size={14} /> Send to {preview.count} Recipients
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent>
          <DialogHeader><DialogTitle>Confirm Send</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Send "{form.subject}" to <strong>{preview?.count}</strong> recipients?</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)}>Cancel</Button>
            <Button onClick={handleSend} disabled={sending} data-testid="blast-confirm-send">{sending ? "Sending..." : "Send Now"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
